import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useAuthStore from "../../../store/useAuthStore";
import { ASSET_CONDITIONS } from "../../../constants/inventory";
import { IoArrowBack } from "react-icons/io5";
import { Skeleton } from "@mui/material";

const ASSET_STATUSES = {
  assigned: { label: "Assigned", color: "bg-blue-100 text-blue-800" },
  in_use: { label: "In Use", color: "bg-green-100 text-green-800" },
  returned: { label: "Returned", color: "bg-gray-100 text-gray-800" },
  damaged: { label: "Damaged", color: "bg-red-100 text-red-800" },
  lost: { label: "Lost", color: "bg-red-100 text-red-800" },
};

const ViewAsset = () => {
  const { token } = useAuthStore();
  const navigate = useNavigate();
  const { id } = useParams();

  const [assignment, setAssignment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const [showReturnModal, setShowReturnModal] = useState(false);
  const [conditionOnReturn, setConditionOnReturn] = useState("good");
  const [returnNotes, setReturnNotes] = useState("");

  const [showDamageModal, setShowDamageModal] = useState(false);
  const [damageType, setDamageType] = useState("damaged");
  const [damageNotes, setDamageNotes] = useState("");

  useEffect(() => {
    fetchAssignment();
  }, [id]);

  const fetchAssignment = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/getAssetAssignment/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setAssignment(data.assignment || data);
      } else {
        setErrorMessage(data.error || "Failed to fetch assignment");
      }
    } catch (err) {
      setErrorMessage("Failed to fetch asset assignment");
    } finally {
      setLoading(false);
    }
  };

  const handleReturn = async () => {
    if (!conditionOnReturn) return;
    setActionLoading(true);
    try {
      const response = await fetch(`/api/returnAsset/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          conditionOnReturn,
          notes: returnNotes || undefined,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        setSuccessMessage("Asset returned successfully");
        setAssignment(data.assignment || data);
        setShowReturnModal(false);
        setConditionOnReturn("good");
        setReturnNotes("");
        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        setErrorMessage(data.error || "Failed to return asset");
        setTimeout(() => setErrorMessage(""), 3000);
      }
    } catch (err) {
      setErrorMessage("Failed to return asset");
      setTimeout(() => setErrorMessage(""), 3000);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDamageOrLoss = async () => {
    setActionLoading(true);
    try {
      const response = await fetch(`/api/assetDamageOrLoss/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          type: damageType,
          notes: damageNotes || undefined,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        setSuccessMessage(`Asset reported as ${damageType}`);
        setAssignment(data.assignment || data);
        setShowDamageModal(false);
        setDamageType("damaged");
        setDamageNotes("");
        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        setErrorMessage(data.error || "Failed to report damage/loss");
        setTimeout(() => setErrorMessage(""), 3000);
      }
    } catch (err) {
      setErrorMessage("Failed to report damage/loss");
      setTimeout(() => setErrorMessage(""), 3000);
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = ASSET_STATUSES[status] || { label: status, color: "bg-gray-100 text-gray-800" };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="m-2 md:m-6 p-2 md:p-6">
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
      </div>
    );
  }

  if (!assignment) {
    return (
      <div className="m-2 md:m-6 p-2 md:p-6 text-center">
        <p className="text-gray-500">Asset assignment not found</p>
      </div>
    );
  }

  const canTakeAction = ["assigned", "in_use"].includes(assignment.status);

  return (
    <div className="m-2 md:m-6 p-2 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/Assets")}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <IoArrowBack className="text-xl text-primary" />
          </button>
          <div>
            <h2 className="text-xl font-semibold text-gray-800">
              {assignment.assetCode || "Asset Assignment"}
            </h2>
            <p className="text-sm text-gray-500">
              Assigned {assignment.assignedDate ? new Date(assignment.assignedDate).toLocaleDateString() : "N/A"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {getStatusBadge(assignment.status)}
        </div>
      </div>

      {/* Messages */}
      {errorMessage && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {errorMessage}
        </div>
      )}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">
          {successMessage}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Assignment Info */}
          <div className="bg-white rounded-lg shadow p-5">
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Assignment Information</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Asset Code</p>
                <p className="font-medium">{assignment.assetCode || "N/A"}</p>
              </div>
              <div>
                <p className="text-gray-500">Serial Number</p>
                <p className="font-medium">{assignment.serialNumber || "N/A"}</p>
              </div>
              <div>
                <p className="text-gray-500">Status</p>
                <p className="font-medium capitalize">{assignment.status || "N/A"}</p>
              </div>
              <div>
                <p className="text-gray-500">Condition</p>
                <p className="font-medium capitalize">{assignment.condition || "N/A"}</p>
              </div>
              <div>
                <p className="text-gray-500">Assigned Date</p>
                <p className="font-medium">
                  {assignment.assignedDate ? new Date(assignment.assignedDate).toLocaleDateString() : "N/A"}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Expected Return</p>
                <p className="font-medium">
                  {assignment.expectedReturnDate
                    ? new Date(assignment.expectedReturnDate).toLocaleDateString()
                    : "N/A"}
                </p>
              </div>
              {assignment.returnedDate && (
                <div>
                  <p className="text-gray-500">Returned Date</p>
                  <p className="font-medium">
                    {new Date(assignment.returnedDate).toLocaleDateString()}
                  </p>
                </div>
              )}
              {assignment.conditionOnReturn && (
                <div>
                  <p className="text-gray-500">Condition on Return</p>
                  <p className="font-medium capitalize">{assignment.conditionOnReturn}</p>
                </div>
              )}
            </div>
          </div>

          {/* Item Details */}
          <div className="bg-white rounded-lg shadow p-5">
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Item Details</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Item Name</p>
                <p className="font-medium">{assignment.item?.name || "N/A"}</p>
              </div>
              <div>
                <p className="text-gray-500">SKU</p>
                <p className="font-medium">{assignment.item?.sku || "N/A"}</p>
              </div>
              <div>
                <p className="text-gray-500">Category</p>
                <p className="font-medium">{assignment.item?.category?.name || "N/A"}</p>
              </div>
              <div>
                <p className="text-gray-500">Unit Cost</p>
                <p className="font-medium">
                  {assignment.item?.unitCost ? `Nu. ${assignment.item.unitCost.toLocaleString()}` : "N/A"}
                </p>
              </div>
            </div>
          </div>

          {/* Assigned To */}
          <div className="bg-white rounded-lg shadow p-5">
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Assigned To</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Employee</p>
                <p className="font-medium">
                  {assignment.assignedTo?.name ||
                    `${assignment.assignedTo?.firstName || ""} ${assignment.assignedTo?.lastName || ""}`.trim() ||
                    "N/A"}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Employee ID</p>
                <p className="font-medium">{assignment.assignedTo?.employeeId || "N/A"}</p>
              </div>
              <div>
                <p className="text-gray-500">Project</p>
                <p className="font-medium">
                  {assignment.project
                    ? `${assignment.project.projectCode || ""} - ${assignment.project.name || ""}`
                    : "N/A"}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Department</p>
                <p className="font-medium">
                  {assignment.department?.deptName || assignment.department || "N/A"}
                </p>
              </div>
            </div>
          </div>

          {/* Notes */}
          {assignment.notes && (
            <div className="bg-white rounded-lg shadow p-5">
              <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Notes</h3>
              <p className="text-sm text-gray-700">{assignment.notes}</p>
            </div>
          )}
        </div>

        {/* Right: Actions */}
        <div className="space-y-6">
          {canTakeAction && (
            <div className="bg-white rounded-lg shadow p-5">
              <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Actions</h3>
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => setShowReturnModal(true)}
                  disabled={actionLoading}
                  className="w-full px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50"
                >
                  Return Asset
                </button>
                <button
                  onClick={() => setShowDamageModal(true)}
                  disabled={actionLoading}
                  className="w-full px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 disabled:opacity-50"
                >
                  Report Damage / Loss
                </button>
              </div>
            </div>
          )}

          {/* Summary Card */}
          <div className="bg-white rounded-lg shadow p-5">
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Summary</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Status</span>
                <span className="font-medium capitalize">{assignment.status}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Condition</span>
                <span className="font-medium capitalize">{assignment.condition}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Days Assigned</span>
                <span className="font-medium">
                  {assignment.assignedDate
                    ? Math.max(
                        0,
                        Math.floor(
                          (new Date(assignment.returnedDate || new Date()) - new Date(assignment.assignedDate)) /
                            (1000 * 60 * 60 * 24)
                        )
                      )
                    : "N/A"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Return Modal */}
      {showReturnModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">Return Asset</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Condition on Return <span className="text-red-500">*</span>
              </label>
              <select
                value={conditionOnReturn}
                onChange={(e) => setConditionOnReturn(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                {ASSET_CONDITIONS.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                value={returnNotes}
                onChange={(e) => setReturnNotes(e.target.value)}
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                placeholder="Any notes about the return"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setShowReturnModal(false); setConditionOnReturn("good"); setReturnNotes(""); }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleReturn}
                disabled={actionLoading}
                className="px-4 py-2 bg-primary text-white rounded-lg text-sm disabled:opacity-50"
              >
                {actionLoading ? "Processing..." : "Confirm Return"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Damage/Loss Modal */}
      {showDamageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">Report Damage / Loss</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type <span className="text-red-500">*</span>
              </label>
              <select
                value={damageType}
                onChange={(e) => setDamageType(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="damaged">Damaged</option>
                <option value="lost">Lost</option>
              </select>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                value={damageNotes}
                onChange={(e) => setDamageNotes(e.target.value)}
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                placeholder="Describe the damage or loss"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setShowDamageModal(false); setDamageType("damaged"); setDamageNotes(""); }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleDamageOrLoss}
                disabled={actionLoading}
                className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm disabled:opacity-50"
              >
                {actionLoading ? "Processing..." : "Submit Report"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewAsset;
